import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GALLERY_CONSTS, ProGallery } from 'pro-gallery';
import { GalleryProps, migrateOptions } from 'pro-gallery-lib';
import { LayoutFixer } from '@wix/pro-gallery-layout-fixer';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import imageSDK from '@wix/image-client-api/dist/imageClientSDK';

import type { GallerySectionViewModel } from '../../../../service-page-view-model/gallery-section-view-model/gallerySectionViewModel';
import settingsParams from '../../settingsParams';
import stylesParams from '../../stylesParams';
import { useVisibilityCheck } from '../../hooks/useVisibilityCheck';
import { useNumberStyleCheck } from '../../hooks/useNumberStyleCheck';
import {
  GalleryArrowPositionOptions,
  GalleryItemSizeOptions,
  GalleryLayoutOptions,
  ImageResizeOptions,
  MainComponents,
} from '../../types';
import { useRenderLocation } from '../useRenderLocation';
import { useScrollBase } from '../useScrollBase';
import classes from './Gallery.scss';

type GalleryComponentProps = {
  viewModel: GallerySectionViewModel;
};

const Gallery: React.FC<GalleryComponentProps> = ({ viewModel }) => {
  const { isMobile, isEditor, isPreview, isEditorX, isViewer } =
    useEnvironment();
  const settings = useSettings();
  const styles = useStyles();
  const { shouldBeVisible } = useVisibilityCheck();
  const { getNumberStyleValue } = useNumberStyleCheck();
  const selectedLayout: GalleryLayoutOptions = settings.get(
    settingsParams.galleryLayout,
  );
  const isSliderImageResizeOptionCrop =
    settings.get(settingsParams.gallerySliderImageResizeOption) ===
    ImageResizeOptions.CROP;

  const galleryItemSizeOptionsToPixels = (
    galleryItemSizeOptions: GalleryItemSizeOptions,
  ) => {
    if (isMobile) {
      return 200;
    }
    switch (galleryItemSizeOptions) {
      case GalleryItemSizeOptions.LARGE:
        return 500;
      case GalleryItemSizeOptions.SMALL:
        return 150;
      case GalleryItemSizeOptions.MEDIUM:
      default:
        return 300;
    }
  };

  const getOptionsByGalleryLayoutOptions = () => {
    const isShowArrows =
      viewModel.items.length > 1 &&
      settings.get(settingsParams.gallerySliderShowArrows);
    switch (selectedLayout) {
      case GalleryLayoutOptions.MASONRY:
        return {
          galleryLayout:
            GALLERY_CONSTS.layoutParams_structure_galleryLayout.MASONRY,
          itemBorderColor: styles.get(
            stylesParams.galleryMasonryItemBorderColor,
          ).value,
          itemBorderWidth: getNumberStyleValue('galleryMasonryItemBorderWidth'),
          itemBorderRadius: getNumberStyleValue('galleryMasonryItemRadius'),
          imageMargin: getNumberStyleValue('galleryMasonryItemSpacing'),
          gallerySizeType: 'px',
          gallerySizePx: galleryItemSizeOptionsToPixels(
            settings.get(settingsParams.galleryMasonryItemSize),
          ),
          hoveringBehaviour:
            GALLERY_CONSTS.behaviourParams_item_overlay_hoveringBehaviour
              .NEVER_SHOW,
        };
      case GalleryLayoutOptions.GRID:
        return {
          galleryLayout:
            GALLERY_CONSTS.layoutParams_structure_galleryLayout.GRID,
          itemBorderColor: styles.get(stylesParams.galleryGridItemBorderColor)
            .value,
          imageMargin: getNumberStyleValue('galleryGridItemSpacing'),
          itemBorderWidth: getNumberStyleValue('galleryGridItemBorderWidth'),
          itemBorderRadius: getNumberStyleValue('galleryGridItemRadius'),
          gallerySizeType: 'px',
          gallerySizePx: settings.get(settingsParams.galleryGridThumbnailSize),
          hoveringBehaviour:
            GALLERY_CONSTS.behaviourParams_item_overlay_hoveringBehaviour
              .NEVER_SHOW,
        };
      case GalleryLayoutOptions.SLIDER:
      default:
        return {
          galleryLayout:
            GALLERY_CONSTS.layoutParams_structure_galleryLayout.SLIDER,
          itemBorderColor: styles.get(stylesParams.gallerySliderItemBorderColor)
            .value,
          imageMargin: getNumberStyleValue('gallerySliderItemSpacing'),
          itemBorderWidth: getNumberStyleValue('gallerySliderItemBorderWidth'),
          itemBorderRadius: getNumberStyleValue('gallerySliderItemRadius'),
          cubeType: GALLERY_CONSTS.layoutParams_crop_method.FILL,
          slideshowLoop: settings.get(settingsParams.gallerySliderLoopImages),
          isAutoSlideshow:
            !isEditor && settings.get(settingsParams.gallerySliderAutoSlide),
          autoSlideshowInterval: settings.get(
            settingsParams.gallerySliderTimeBetweenImages,
          ),
          autoSlideshowType: 'interval',
          showArrows: isShowArrows,
          arrowsSize: settings.get(settingsParams.gallerySliderArrowsSize),
          arrowsPosition:
            settings.get(settingsParams.gallerySliderArrowsPosition) ===
              GalleryArrowPositionOptions.INSIDE || !isShowArrows
              ? GALLERY_CONSTS.layoutParams_thumbnails_position.ON_GALLERY
              : GALLERY_CONSTS.layoutParams_thumbnails_position.OUTSIDE_GALLERY,
          hoveringBehaviour:
            GALLERY_CONSTS.behaviourParams_item_overlay_hoveringBehaviour
              .NEVER_SHOW,
          cubeRatio: isSliderImageResizeOptionCrop ? '1' : '100%/100%',
        };
    }
  };

  const getViewMode = () => {
    if (isViewer) {
      return GALLERY_CONSTS.viewMode.SITE;
    }
    if (isPreview) {
      return GALLERY_CONSTS.viewMode.PREVIEW;
    }
    if (isEditorX || isEditor) {
      return GALLERY_CONSTS.viewMode.EDIT;
    }
  };

  const items = viewModel.items.map(
    ({ height, width, relativeUri, altText }, i) => ({
      itemId: `sample-id-${i}`,
      mediaUrl: relativeUri,
      metaData: {
        alt: altText,
        type: 'image',
        height,
        width,
      },
    }),
  );
  if (!items.length) {
    return null;
  } else {
    const viewMode = getViewMode();
    const options = migrateOptions(getOptionsByGalleryLayoutOptions());
    const container = {
      height: Number(
        selectedLayout === GalleryLayoutOptions.SLIDER &&
          settings.get(settingsParams.gallerySliderHeight),
      ),
      width: shouldBeVisible('sidebarVisibility') ? (isMobile ? 280 : 0) : 720,
    };

    return (
      <GalleryWrapper
        items={items}
        viewMode={viewMode as Props['viewMode']}
        options={options as any}
        container={container!}
      />
    );
  }
};
const GALLERY_ID = 'service-page-gallery-section';

/**
 * In css-per-breakpoint mode the sidebar section is rendered both in the body
 * and in the sidebar, so the gallery id has to be unique per render location -
 * otherwise both copies emit the same DOM ids and the same LayoutFixer styles.
 */
const getGalleryId = (renderLocation: MainComponents): string =>
  renderLocation === MainComponents.BODY
    ? GALLERY_ID
    : `${GALLERY_ID}-${renderLocation.toLowerCase()}`;

const createMediaUrl = ({
  item,
  originalUrl,
  requiredWidth,
  requiredHeight,
}): string => {
  // relativeUrl, sourceWidth, sourceHeight, targetWidth, targetHeight, options
  return imageSDK.getScaleToFillImageURL(
    originalUrl,
    item.metaData.width,
    item.metaData.height,
    requiredWidth,
    requiredHeight,
  );
};

type Props = Pick<GalleryProps, 'items' | 'options' | 'container' | 'viewMode'>;
export const GalleryWrapper: React.FC<Props> = ({
  items,
  container: defaultContainer,
  viewMode,
  options,
}) => {
  const renderLocation = useRenderLocation();
  const galleryId = getGalleryId(renderLocation);
  const [container, setContainer] = useState(defaultContainer);
  useEffect(() => {
    setContainer((prev) => ({
      ...prev,
      height: defaultContainer.height,
    }));
  }, [defaultContainer.height]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollBase = useScrollBase(containerRef);
  const { isSSR } = useEnvironment();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const setRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!node) return;
    resizeObserverRef.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainer((prev) => ({ ...prev, width: entry.contentRect.width }));
      }
    });
    resizeObserverRef.current.observe(node);
  }, []);
  const isPrerenderMode = !container.width;
  return (
    <div
      className={classes.wixGalleryWrapper}
      style={{ width: '100%' }}
      ref={setRef}
      data-hook="gallery-section"
    >
      <ProGallery
        id={galleryId}
        viewMode={viewMode}
        items={items}
        options={options}
        isPrerenderMode={isPrerenderMode}
        container={{ ...container, scrollBase }}
        createMediaUrl={createMediaUrl}
        totalItemsCount={items.length}
        scrollingElement={isSSR ? null : window}
      />
      {isPrerenderMode && (
        <LayoutFixer
          id={galleryId}
          items={items}
          options={options}
          isPrerenderMode={isPrerenderMode}
        />
      )}
    </div>
  );
};
GalleryWrapper.displayName = 'GalleryWrapper';

export default Gallery;
